import React from 'react';
import { PRICING } from '../pricing';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SpeedIcon from '@mui/icons-material/Speed';
import PeopleIcon from '@mui/icons-material/People';
import LanIcon from '@mui/icons-material/Lan';
import CodeIcon from '@mui/icons-material/Code';
import ExtensionIcon from '@mui/icons-material/Extension';
import LayersIcon from '@mui/icons-material/Layers';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import FlashOnIcon from '@mui/icons-material/FlashOn';

export const react = {
  title: "React Mastery",
  subtitle: "Framework Excellence",
  icon: <CodeIcon sx={{ fontSize: 40, color: '#61dafb' }} />,
  description: "Architect high-performance web applications. Curriculum sourced directly from real-world FAANG interview experiences.",
  features: [
    "50+ Deep Dive React Scenarios",
    "Hooks & Fiber Mechanics",
    "State Management Patterns",
    "Based on Real Interviews"
  ],
  price: PRICING.react.price,
  mrp: PRICING.react.mrp,
  topics: [
    {
      id: "react_fundamentals",
      title: "React Fundamentals",
      desc: "Master the foundational component models, state types, and pure rendering.",
      badge: "Basics",
      icon: <DashboardIcon />,
      group: "React Fundamentals",
      problems: [
        { id: "react_comp_types", title: "Component Types (Functional vs Class)", badge: "Core", fileUrl: "/documents/react/react_comp_types.md" },
        { id: "react_comp_pure", title: "Pure vs Impure Components", badge: "Purity", fileUrl: "/documents/react/react_comp_pure.md" },
        { id: "react_hooks_state", title: "State Management Foundations", badge: "State", fileUrl: "/documents/react/react_hooks_state.md" },
        { id: "react_comp_control", title: "Props & Component Control", badge: "Control", fileUrl: "/documents/react/react_comp_control.md" }
      ]
    },
    {
      id: "react_core",
      title: "Core Mechanics",
      desc: "Deep dive into the Virtual DOM, Fiber architecture, and reconciliation.",
      badge: "Expert",
      icon: <LayersIcon />,
      group: "React Core",
      problems: [
        { id: "react_vdom", title: "Virtual DOM & Reconciliation", badge: "VDOM", fileUrl: "/documents/react/react_vdom.md" },
        { id: "react_fiber", title: "Fiber Architecture & Work Loop", badge: "Fiber", fileUrl: "/documents/react/react_fiber.md" },
        { id: "react_hooks_internals", title: "Hooks Internals & Execution Order", badge: "Expert", fileUrl: "/documents/react/react_hooks_internals.md" },
        { id: "react_18_future", title: "Concurrent Mode & React 18+", badge: "Future", fileUrl: "/documents/react/react_18_future.md" }
      ]
    },
    {
      id: "react_hooks_mastery",
      title: "Hooks Mastery",
      desc: "Exhaustive deep dives into every essential React hook and its behavior.",
      badge: "Deep Dive",
      icon: <ExtensionIcon />,
      group: "Hooks Mastery",
      problems: [
        { id: "react_useState_deepdive", title: "useState Under the Hood", badge: "State", fileUrl: "/documents/react/react_useState_deepdive.md" },
        { id: "react_useEffect_deepdive", title: "useEffect Lifecycle Mastery", badge: "Effects", fileUrl: "/documents/react/react_useEffect_deepdive.md" },
        { id: "react_usecontext_deepdive", title: "useContext & State Injection", badge: "Context", fileUrl: "/documents/react/react_usecontext_deepdive.md" },
        { id: "react_usereducer_deepdive", title: "useReducer for Complex State", badge: "Reducer", fileUrl: "/documents/react/react_usereducer_deepdive.md" },
        { id: "react_useref_deepdive", title: "useRef & Imperative Actions", badge: "Ref", fileUrl: "/documents/react/react_useref_deepdive.md" },
        { id: "react_custom_hooks_deepdive", title: "Custom Hooks & Logic Reuse", badge: "Abstr", fileUrl: "/documents/react/react_custom_hooks_deepdive.md" }
      ]
    },
    {
      id: "react_performance",
      title: "Performance & Optimization",
      desc: "Eliminate wasted renders and master memoization techniques.",
      badge: "Perf",
      icon: <SpeedIcon />,
      group: "Performance",
      problems: [
        { id: "react_perf_memo", title: "React.memo & Selective Rendering", badge: "Memo", fileUrl: "/documents/react/react_perf_memo.md" },
        { id: "react_perf_usememo", title: "useMemo & Expensive Calcs", badge: "Logic", fileUrl: "/documents/react/react_perf_usememo.md" },
        { id: "react_perf_usecallback", title: "useCallback & Stable Refs", badge: "Events", fileUrl: "/documents/react/react_perf_usecallback.md" },
        { id: "react_optimization", title: "Render Cycle Optimization", badge: "Perf", fileUrl: "/documents/react/react_optimization.md" }
      ]
    },
    {
      id: "react_patterns_ecosystem",
      title: "Advanced Patterns & Ecosystem",
      desc: "Master industry-standard patterns and essential libraries for scale.",
      badge: "Architect",
      icon: <LanIcon />,
      group: "Advanced Patterns",
      problems: [
        { id: "react_advanced_hocs", title: "Higher Order Components (HOCs)", badge: "HOC", fileUrl: "/documents/react/react_advanced_hocs.md" },
        { id: "react_advanced_render_props", title: "Render Props & Inversion", badge: "Render", fileUrl: "/documents/react/react_advanced_render_props.md" },
        { id: "react_advanced_patterns", title: "Compound Components & Patterns", badge: "UI Arch", fileUrl: "/documents/react/react_advanced_patterns.md" },
        { id: "react_redux", title: "Redux & Modern State Management", badge: "Redux", fileUrl: "/documents/react/react_redux.md" },
        { id: "react_router", title: "React Router & Navigation", badge: "Router", fileUrl: "/documents/react/react_router.md" },
        { id: "react_query", title: "TanStack Query (React Query)", badge: "Query", fileUrl: "/documents/react/react_query.md" },
        { id: "react_nextjs_basics", title: "Next.js Fundamentals (SSR/SSG)", badge: "Next.js", fileUrl: "/documents/react/react_nextjs_basics.md" },
        { id: "react_project_api_fetch", title: "API Fetching & Project Design", badge: "Project", fileUrl: "/documents/react/react_project_api_fetch.md" },
        { id: "react_project_forms", title: "Complex Form State Management", badge: "Forms", fileUrl: "/documents/react/react_project_forms.md" },
        { id: "react_project_query", title: "Query-based Application Arch", badge: "Project", fileUrl: "/documents/react/react_project_query.md" }
      ]
    },
    {
      id: "react_senior",
      title: "Senior React & Production",
      desc: "Real-world testing, interview preparation, and arch case studies.",
      badge: "Senior",
      icon: <MilitaryTechIcon />,
      group: "Senior React",
      problems: [
        { id: "react_testing", title: "Testing with RTL & Jest", badge: "Test", fileUrl: "/documents/react/react_testing.md" }
      ]
    },
    {
      id: "react_19_modern",
      title: "React 19 & Next-Gen",
      desc: "Master Server Components, Actions, and modern high-performance architectures.",
      badge: "Next-Gen",
      icon: <FlashOnIcon />,
      group: "React 19 & Next-Gen",
      problems: [
        { id: "react_server_components", title: "React Server Components (RSC)", badge: "Arch", fileUrl: "/documents/react/react_server_components.md" },
        { id: "react_actions_transitions", title: "Server Actions & Transitions", badge: "Modern", fileUrl: "/documents/react/react_actions_transitions.md" },
        { id: "react_modern_state", title: "Global State: Zustand vs Context", badge: "State", fileUrl: "/documents/react/react_modern_state.md" }
      ]
    },
    {
      id: "react_interview_prep",
      title: "Interview Preparation",
      desc: "Master high-frequency questions and deep-dive enterprise scenarios.",
      badge: "Prep",
      icon: <PeopleIcon />,
      group: "React Practice",
      problems: [
        { id: "react_qa_mastery", title: "React Interview Q&A", badge: "Core", fileUrl: "/documents/react/react_interview.md" },
        { id: "react_enterprise_scenarios", title: "Real-World Enterprise Scenarios", badge: "Senior", fileUrl: "/documents/react/react_enterprise_scenarios.md" },
        { id: "react_case_studies", title: "Architecture Case Studies", badge: "Expert", fileUrl: "/documents/react/react_case_studies.md" }
      ]
    }
  ]
};
